import React, { useState, useRef, useEffect } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import BottomSheet from './BottomSheet';

export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'اختر...',
  error,
  style,
  /** Show clear (×) when a non-empty value is selected — restores placeholder / empty option */
  allowClear = false,
  clearValue = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);

  const selectedOption = options.find(o => o.value === value || o.value === Number(value))
    || options.find(o => String(o.value) === String(value));
  const hasValue = value !== undefined && value !== null && String(value) !== '' && String(value) !== String(clearValue);
  const filteredOptions = search
    ? options.filter(o => (o.label || '').toLowerCase().includes(search.toLowerCase()))
    : options;

  function open() {
    setSearch('');
    setIsOpen(true);
  }

  function close() {
    setSearch('');
    setIsOpen(false);
  }

  function handleSelect(opt) {
    onChange(opt.value);
    close();
  }

  function handleClear(e) {
    e.preventDefault();
    e.stopPropagation();
    onChange(clearValue);
    close();
  }

  useEffect(() => {
    if (isMobile || !isOpen) return;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        close();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isMobile, isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const triggerStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid ' + (error ? '#f44336' : '#ddd'),
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: 'white',
    textAlign: 'right',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: selectedOption ? '#333' : '#999',
    outline: 'none',
    minHeight: '42px',
    ...style,
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={triggerStyle} onClick={open} tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginRight: 4 }}>
          {allowClear && hasValue && (
            <button
              type="button"
              aria-label="مسح الاختيار"
              title="مسح — عرض الكل"
              onClick={handleClear}
              style={{
                border: 'none',
                background: '#f0edf5',
                color: '#5c4d8a',
                width: 22,
                height: 22,
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                fontWeight: 700,
              }}
            >
              ×
            </button>
          )}
          <span style={{ fontSize: '10px', color: '#999' }}>▼</span>
        </span>
      </div>

      {isOpen && !isMobile && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          backgroundColor: 'white', border: '1px solid #ddd',
          borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 100, maxHeight: '300px', display: 'flex',
          flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
            <input
              ref={searchInputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="بحث..."
              dir="auto"
              style={{
                width: '100%', padding: '8px 10px', border: '1px solid #ddd',
                borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box',
                outline: 'none',
              }}
              onKeyDown={e => {
                e.stopPropagation();
                if (e.key === 'Escape') close();
                if (e.key === 'Enter' && filteredOptions.length === 1) {
                  handleSelect(filteredOptions[0]);
                }
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '13px' }}>لا توجد نتائج</div>
            ) : (
              filteredOptions.map((opt, i) => (
                <div
                  key={opt.value != null ? opt.value : i}
                  onClick={() => handleSelect(opt)}
                  style={{
                    padding: '10px 12px', cursor: 'pointer', fontSize: '14px',
                    backgroundColor: opt.value === value || String(opt.value) === String(value) ? '#EDE7F6' : 'transparent',
                    color: '#333', borderBottom: i < filteredOptions.length - 1 ? '1px solid #f5f5f5' : 'none',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = (opt.value === value || String(opt.value) === String(value)) ? '#EDE7F6' : 'transparent';
                  }}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {isOpen && isMobile && (
        <BottomSheet isOpen={isOpen} onClose={close}>
          <div style={{ padding: '0 0 var(--sp-md)' }}>
            <input
              ref={searchInputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="بحث..."
              dir="auto"
              style={{
                width: '100%', padding: '12px', border: '1px solid #ddd',
                borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box',
                outline: 'none', marginBottom: '12px',
              }}
            />
            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>لا توجد نتائج</div>
              ) : (
                filteredOptions.map((opt, i) => (
                  <div
                    key={opt.value != null ? opt.value : i}
                    onClick={() => handleSelect(opt)}
                    style={{
                      padding: '14px 12px', cursor: 'pointer', fontSize: '16px',
                      backgroundColor: opt.value === value || String(opt.value) === String(value) ? '#EDE7F6' : 'transparent',
                      color: '#333', borderBottom: '1px solid #f0f0f0',
                      borderRadius: '8px',
                    }}
                  >
                    {opt.label}
                  </div>
                ))
              )}
            </div>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}
